import { ModernPaginationProps } from '@moneyforward/ap-frontend-components';
import { RefetchOptions } from '@tanstack/react-query';
import {
  CommonError,
  ErrorWithLabelMessage,
  InvoiceReportOrderByDirection,
  InvoiceReportOrderByField,
  SearchInvoiceReportsQuery,
  SearchInvoiceReportsQueryMfFile,
  SearchInvoiceReportsQueryPaymentStatus,
  SearchInvoiceReportsQueryStatus,
  SearchedInvoiceReportPaymentStatus,
  SearchedInvoiceReportStatus,
} from '@/context/services/admin_invoice_reports/AdminInvoiceReportsQuery.enums';

import { Dayjs } from 'dayjs';

/** Action state from API: visible = show or not, allowed = enabled, reason = tooltip when disabled. */
export interface ReportActionState {
  visible: boolean;
  allowed: boolean;
  reason: string | null;
}

export interface AdminInvoiceReportColumn {
  [key: string]: unknown;
  id: string;
  number: string;
  created_at: string;
  submitted_at?: string | null;
  office_member: {
    name: string;
    number: string;
  };
  title: string;
  status: SearchedInvoiceReportStatus;
  ap_payee_name?: string | null;
  book_date?: string | null;
  due_date?: string | null;
  total_amount?: string | null;
  foreign_currency_amount?: string | null;
  report_approval_names?: string | null;
  approved_at?: string | null;
  payment_date?: string | null;
  payment_status?: SearchedInvoiceReportPaymentStatus;
  invoice_file?: {
    name: string;
    receipt_type: string;
  };
  error_messages?: ErrorWithLabelMessage[] | null;
  detail_path: string;
  edit_path: string;
  copy_path: string;
  is_copyable: boolean;
  edit_action: ReportActionState;
  delete_action: ReportActionState;
  cancel_action: ReportActionState;
}

// Hardcoded payment method options (支払方法) — 11 options
export const ADMIN_INVOICE_REPORT_PAYMENT_METHOD = {
  bank_transfer: 'bank_transfer',
  cash: 'cash',
  payment_slip: 'payment_slip',
  automatic_debit: 'automatic_debit',
  credit_card: 'credit_card',
  prepaid_card: 'prepaid_card',
  check: 'check',
  promissory_note: 'promissory_note',
  electronic_bill: 'electronic_bill',
  overseas_remittance: 'overseas_remittance',
  others: 'others',
} as const;

export type AdminInvoiceReportPaymentMethod =
  (typeof ADMIN_INVOICE_REPORT_PAYMENT_METHOD)[keyof typeof ADMIN_INVOICE_REPORT_PAYMENT_METHOD];

/**
 * Form state for the admin invoice reports search form.
 * Fields applicant_ids, applicant_department_ids, department_ids, counterparty_ids, payment_method
 * are present in the form but excluded from the API query (dummy data — backend integration deferred).
 */
export interface AdminInvoiceReportsSearchFormData {
  // Text inputs
  query: string;
  number: string;
  // Status selects
  status: SearchInvoiceReportsQueryStatus | null;
  payment_status: SearchInvoiceReportsQueryPaymentStatus | null;
  mf_file: SearchInvoiceReportsQueryMfFile | null;
  // Multi-selects (dummy data — excluded from API query)
  applicant_ids: string[];
  applicant_department_ids: string[];
  department_ids: string[];
  counterparty_ids: string[];
  // Multi-select (real — wired to API)
  ap_payee_ids: string[];
  // Single select (dummy data — hardcoded options, excluded from API query)
  payment_method: AdminInvoiceReportPaymentMethod | null;
  // Amount range
  total_amount_from: string;
  total_amount_to: string;
  // Date ranges
  created_date: (string | Dayjs)[];
  submitted_date: (string | Dayjs)[];
  approved_date: (string | Dayjs)[];
  book_date: (string | Dayjs)[];
  due_date: (string | Dayjs)[];
  payment_date: (string | Dayjs)[];
  // Sort / pagination (passed through from table state)
  sort_fields: string[] | undefined;
  sort_orders: string[] | undefined;
  page: number;
}

/**
 * API query shape sent to the search endpoint.
 * Maps 1:1 to SearchInvoiceReportsQuery from ap-openapi (shared endpoint, transitional).
 * New fields that the backend does not yet support are intentionally omitted.
 */
export interface AdminInvoiceReportsSearchQuery {
  query?: string;
  number?: string;
  status?: SearchInvoiceReportsQueryStatus;
  ap_payee_ids?: string[];
  total_amount_from?: string;
  total_amount_to?: string;
  payment_status?: SearchInvoiceReportsQueryPaymentStatus;
  mf_file?: SearchInvoiceReportsQueryMfFile;
  created_at_from?: string;
  created_at_to?: string;
  submitted_at_from?: string;
  submitted_at_to?: string;
  approved_at_from?: string;
  approved_at_to?: string;
  book_date_from?: string;
  book_date_to?: string;
  due_date_from?: string;
  due_date_to?: string;
  payment_date_from?: string;
  payment_date_to?: string;
  sort_fields?: string[];
  sort_orders?: string[];
  page?: number;
}

export type SortField =
  | 'number'
  | 'created_at'
  | 'submitted_at'
  | 'book_date'
  | 'due_date'
  | 'approved_at'
  | 'payment_date';

export type OrderByItem = {
  field: InvoiceReportOrderByField;
  direction: InvoiceReportOrderByDirection;
};

export type FetchAdminInvoiceReportsArgs = {
  page?: number;
  perPage?: number;
  query?: SearchInvoiceReportsQuery;
  order_by?: OrderByItem[];
  onPageChange?: (page: number) => void;
};

export type AdminInvoiceReportsSearchRefetch = (
  options?: RefetchOptions
) => Promise<unknown>;

export type FetchAdminInvoiceReportsResult = {
  pagination: ModernPaginationProps;
  data: AdminInvoiceReportColumn[];
  refetch: AdminInvoiceReportsSearchRefetch;
  isFetching?: boolean;
  isError: boolean;
  error: CommonError | undefined;
};

export const SORT_FIELD_MAPPING: Record<SortField, InvoiceReportOrderByField> =
  {
    number: 'number',
    created_at: 'created_date',
    submitted_at: 'submitted_date',
    book_date: 'invoice_book_date',
    due_date: 'invoice_due_date',
    approved_at: 'approved_date',
    payment_date: 'payment_date',
  };

export const mapColumnKeyToApiSortField = (
  field: SortField
): InvoiceReportOrderByField => {
  return SORT_FIELD_MAPPING[field];
};
